import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useCurrency } from '../hooks/useCurrency';
const IDLE_DELAY_MS = 45000;
const PRICING_IDLE_MS = 20000;
const SCROLL_BACK_THRESHOLD = 600;
const SECOND_CHANCE_DELAY_MS = 30000; // 30s after first dismiss
const MAX_SHOWS = 2;
const DISCOUNT_CODE = 'EXTRA20';
const DISCOUNTED_PRICE = 14;
const ORIGINAL_PRICE = 17;
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/9kfBDM9XGTP6kXkwGVeVWH/kndd_hero_thenap2.png';
function getShowCount(): number {
  try {
    return parseInt(sessionStorage.getItem('discount_show_count') || '0', 10);
  } catch {
    return 0;
  }
}
function setStoredCount(n: number) {
  sessionStorage.setItem('discount_show_count', String(n));
}
export function DiscountPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [showCount, setShowCount] = useState(getShowCount);
  const {
    formatPrice
  } = useCurrency();
  const lastScrollY = useRef(0);
  const hasSeenPricing = useRef(false);
  const pricingIdleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const secondChanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMaxed = showCount >= MAX_SHOWS;
  const isSecondShow = showCount >= 1;
  const showPopup = useCallback(() => {
    setShowCount(prev => {
      if (prev >= MAX_SHOWS) return prev;
      const next = prev + 1;
      setStoredCount(next);
      return next;
    });
    setIsOpen(true);
  }, []);
  // Guard: should we listen for triggers?
  const shouldListen = !isMaxed && !isOpen;
  const cancelAll = useCallback(() => {
    setStoredCount(MAX_SHOWS);
    setShowCount(MAX_SHOWS);
    setIsOpen(false);
    if (pricingIdleTimer.current) clearTimeout(pricingIdleTimer.current);
    if (secondChanceTimer.current) clearTimeout(secondChanceTimer.current);
  }, []);
  // ─── Trigger 1: General idle timer (45s fallback) ───
  useEffect(() => {
    if (!shouldListen) return;
    const timer = setTimeout(showPopup, IDLE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [shouldListen, showPopup]);
  // ─── Trigger 2: Exit intent — mouse leaves viewport (desktop) ───
  useEffect(() => {
    if (!shouldListen) return;
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) showPopup();
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [shouldListen, showPopup]);
  // ─── Trigger 3: Rapid scroll-back to top (mobile exit signal) ───
  useEffect(() => {
    if (!shouldListen) return;
    let scrollSamples: number[] = [];
    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const delta = lastScrollY.current - currentY;
        scrollSamples.push(delta);
        if (scrollSamples.length > 5) scrollSamples.shift();
        const totalUpward = scrollSamples.reduce((sum, d) => sum + Math.max(0, d), 0);
        const scrollPercent = currentY / (document.documentElement.scrollHeight - window.innerHeight);
        if (totalUpward > SCROLL_BACK_THRESHOLD && scrollPercent < 0.15) {
          showPopup();
        }
        lastScrollY.current = currentY;
        rafId = null;
      });
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [shouldListen, showPopup]);
  // ─── Trigger 4: Idle after seeing pricing section (20s) ───
  useEffect(() => {
    if (!shouldListen) return;
    const pricingEl = document.getElementById('pricing');
    if (!pricingEl) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasSeenPricing.current) {
          hasSeenPricing.current = true;
          pricingIdleTimer.current = setTimeout(() => {
            showPopup();
          }, PRICING_IDLE_MS);
        }
      });
    }, {
      threshold: 0.5
    });
    observer.observe(pricingEl);
    return () => {
      observer.disconnect();
      if (pricingIdleTimer.current) clearTimeout(pricingIdleTimer.current);
    };
  }, [shouldListen, showPopup]);
  // ─── Trigger 5: Tab visibility change ───
  useEffect(() => {
    if (!shouldListen) return;
    let leftAt: number | null = null;
    const handleVisibility = () => {
      if (document.hidden) {
        leftAt = Date.now();
      } else if (leftAt) {
        const awayMs = Date.now() - leftAt;
        const scrollPercent = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
        if (awayMs > 3000 && scrollPercent > 0.3) {
          showPopup();
        }
        leftAt = null;
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [shouldListen, showPopup]);
  // ─── Cancel permanently if user clicks any CTA ───
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-cta]') || target.closest('a[href*="gemmamama"]') || target.closest('button[class*="bg-sage"]') || target.closest('button[class*="bg-terracotta"]')) {
        cancelAll();
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [cancelAll]);
  // ─── Lock scroll when open ───
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const handleClose = () => {
    setIsOpen(false);
    // If we haven't hit max, schedule a second chance
    if (showCount < MAX_SHOWS) {
      secondChanceTimer.current = setTimeout(() => {
        showPopup();
      }, SECOND_CHANCE_DELAY_MS);
    }
  };
  const handleClaim = () => {
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'InitiateCheckout', {
        content_name: 'The Nap-Trapped Reset — Extra 20% Off',
        value: DISCOUNTED_PRICE,
        currency: 'USD'
      });
    }
    window.open(`https://www.gemmamama.com/cart/45389317111947:1?discount=${DISCOUNT_CODE}`, '_blank');
    cancelAll();
  };
  // Cleanup second chance timer on unmount
  useEffect(() => {
    return () => {
      if (secondChanceTimer.current) clearTimeout(secondChanceTimer.current);
    };
  }, []);
  return <AnimatePresence data-id="element-89">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.35
      }} onClick={handleClose} className="fixed inset-0 bg-brown/30 backdrop-blur-sm z-[60]" data-id="element-90" />

          {/* Modal */}
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-5 pointer-events-none" data-id="element-91">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 16
        }} transition={{
          duration: 0.45,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="w-full max-w-[360px] overflow-hidden pointer-events-auto rounded-3xl relative border border-brown/[0.05]" style={{
          background: 'linear-gradient(168deg, #FFFFFF 0%, #FAF7F2 55%, #F5F0E8 100%)',
          boxShadow: '0 24px 80px rgba(61,50,41,0.14), 0 4px 24px rgba(61,50,41,0.06)'
        }} data-id="element-92">
              {/* Top accent strip */}
              <div className="h-[2px] w-full" style={{
            background: 'linear-gradient(90deg, transparent 10%, rgba(198,114,76,0.3) 50%, transparent 90%)'
          }} data-id="element-93" />

              {/* Warm ambient glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-28 pointer-events-none" style={{
            background: 'radial-gradient(ellipse at top, rgba(198,114,76,0.05) 0%, transparent 70%)'
          }} data-id="element-94" />

              {/* Close button */}
              <button onClick={handleClose} className="absolute top-3.5 right-3.5 z-20 w-7 h-7 flex items-center justify-center rounded-full bg-brown/[0.03] hover:bg-brown/[0.07] transition-colors duration-200 text-brown-light/35 hover:text-brown/55" data-id="element-95">
                <X className="w-3.5 h-3.5" strokeWidth={1.5} data-id="element-96" />
              </button>

              <div className="px-7 pt-8 pb-7 text-center relative z-10" data-id="element-97">
                {/* Value badge — elegant stamp */}
                <div className="relative mx-auto w-[100px] h-[100px] mb-6" data-id="element-98">
                  <div className="absolute inset-0 rounded-full" style={{
                background: 'linear-gradient(145deg, rgba(198,114,76,0.1) 0%, rgba(198,114,76,0.03) 100%)',
                border: '1.5px solid rgba(198,114,76,0.15)'
              }} data-id="element-99" />
                  <div className="absolute inset-[5px] rounded-full" style={{
                border: '1px dashed rgba(198,114,76,0.12)'
              }} data-id="element-100" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-101">
                    <span className="text-brown/25 text-[11px] font-serif line-through leading-none" data-id="element-102">
                      {formatPrice(68)}
                    </span>
                    <span className="text-terracotta font-serif text-[22px] font-bold leading-none mt-0.5" data-id="element-103">
                      {formatPrice(DISCOUNTED_PRICE)}
                    </span>
                    <span className="text-brown-light/35 text-[8px] uppercase tracking-[0.15em] mt-1 font-medium" data-id="element-104">
                      Today only
                    </span>
                  </div>
                </div>

                {/* Ornamental divider */}
                <div className="flex items-center justify-center gap-2.5 mb-5" data-id="element-105">
                  <div className="w-6 h-px bg-gradient-to-r from-transparent to-terracotta/20" data-id="element-106" />
                  <span className="text-terracotta/25 text-[9px] tracking-[0.3em]" data-id="element-107">
                    ✦
                  </span>
                  <div className="w-6 h-px bg-gradient-to-l from-transparent to-terracotta/20" data-id="element-108" />
                </div>

                {/* Headline */}
                <h2 className="font-serif text-[1.4rem] md:text-2xl text-brown leading-snug mb-3" data-id="element-109">
                  Your peace shouldn't have to wait.
                </h2>

                {/* Body */}
                <p className="text-brown-light/60 text-[14px] leading-relaxed mb-6 max-w-[260px] mx-auto" data-id="element-110">
                  You were so close to a calmer postpartum. Grab the Nap-Trapped
                  Reset now for{' '}
                  <span className="font-semibold text-terracotta/80" data-id="element-111">
                    20% OFF
                  </span>{' '}
                  and start feeling like yourself again today.
                </p>

                {/* Price — total value crossed out, 20% off price */}
                <div className="flex items-baseline justify-center gap-2 mb-1" data-id="element-112">
                  <span className="font-serif text-sm text-brown/25 line-through" data-id="element-113">
                    {formatPrice(68)}
                  </span>
                  <span className="font-serif text-[1.75rem] font-bold text-terracotta tracking-tight" data-id="element-114">
                    {formatPrice(DISCOUNTED_PRICE)}
                  </span>
                </div>

                <p className="text-brown-light/30 text-[10px] uppercase tracking-[0.18em] font-medium mb-7" data-id="element-115">
                  One-time payment · Instant access
                </p>

                {/* CTA */}
                <div className="relative mb-4" data-id="element-116">
                  <div className="absolute -inset-0.5 bg-terracotta/15 rounded-xl blur-sm" data-id="element-117" />
                  <motion.button onClick={handleClaim} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className="relative w-full py-3.5 rounded-xl font-bold text-[13px] uppercase tracking-[0.1em] text-white cursor-pointer transition-all duration-300" style={{
                background: 'linear-gradient(135deg, #c4856a 0%, #b07258 100%)',
                boxShadow: '0 3px 12px rgba(198,114,76,0.25)'
              }} data-id="element-118">
                    CLAIM MY CALM NOW →
                  </motion.button>
                </div>

                {/* Dismiss */}
                <button onClick={handleClose} className="mt-4 text-[11px] text-brown-light/25 hover:text-brown-light/45 transition-colors duration-200" data-id="element-119">
                  No thanks, I'll figure it out on my own
                </button>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}